import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TempCodeDialog from "@/components/TempCodeDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SUPER_ADMIN } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";

const COURSE_BG =
  "/brand/proshop-bags-clubs.jpg";

export default function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Forgot-password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);

  // Master-override state — single-input break-glass login that bypasses
  // email+password if the platform owner has lost access. Validates server-side
  // against SUPER_ADMIN_MASTER_OVERRIDE_CODE and audit-logs every attempt.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [tempCodeOpen, setTempCodeOpen] = useState(false);
  const [overrideCode, setOverrideCode] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [overrideError, setOverrideError] = useState("");

  useEffect(() => {
    if (user?.role === "super_admin") navigate("/super-admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/super-admin/login", { email, password });
      setUser(data.user);
      navigate("/super-admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestForgot = async () => {
    setForgotBusy(true);
    setForgotMsg(null);
    try {
      const { data } = await api.post("/super-admin/forgot-password", {
        email: (forgotEmail || email).trim(),
      });
      setForgotMsg({
        type: "success",
        text: data.detail || "If the email is registered, a reset link has been sent.",
      });
    } catch (err) {
      setForgotMsg({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setForgotBusy(false);
    }
  };

  const submitOverride = async () => {
    setOverrideError("");
    setOverrideBusy(true);
    try {
      const { data } = await api.post("/super-admin/master-override", { code: overrideCode.trim() });
      setUser(data.user);
      navigate("/super-admin", { replace: true });
    } catch (err) {
      setOverrideError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setOverrideBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1a14] relative overflow-hidden text-[#F9F8F4]">
      <img src={COURSE_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f1a14] via-[#1A422D]/85 to-[#0f1a14]" />

      {/* EST. 1928 hallmark */}
      <div
        className="hidden sm:block absolute top-10 right-10 text-right fade-up"
        style={{ animationDelay: "0.05s" }}
      >
        <div className="text-[12px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#D9C68A" }}>
          Established
        </div>
        <div className="font-serif italic text-2xl text-[#F9F8F4] mt-1">Anno 2026</div>
      </div>

      <div className="relative max-w-md mx-auto px-6 py-16 sm:py-24 fade-up">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="My Crest Club"
            data-testid="salogin-logo"
            className="h-24 sm:h-28 mx-auto mb-4 drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
          />
          <div className="text-[13px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#D9C68A" }}>Platform Operator</div>
          <h1 className="font-serif text-6xl sm:text-7xl text-[#F9F8F4] mt-4 leading-none">Platform Administrator</h1>
          <p className="mt-5 text-[#D9D2B6] text-base max-w-sm mx-auto leading-relaxed">
            Sign in to review system health, audit activity, and manage the platform.
          </p>
        </div>

        {error && (
          <div className="mt-8 px-4 py-3 border border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4] text-sm">
            {error}
          </div>
        )}

        <form
          onSubmit={submit}
          className="mt-10 border border-[#B59A5A]/40 bg-[#0f1a14]/70 backdrop-blur-sm p-8 sm:p-10"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-[13px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: "#D9C68A" }}>
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={SUPER_ADMIN.loginEmail}
                className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] placeholder-[#7a7a6a] h-12 px-3 focus:outline-none focus:border-[#B59A5A] text-base"
                placeholder="name@mycrestclub.com"
              />
            </div>
            <div>
              <label className="block text-[13px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: "#D9C68A" }}>
                Password
              </label>
              <PasswordInput
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={SUPER_ADMIN.loginPassword}
                eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] placeholder-[#7a7a6a] h-12 px-3 focus:outline-none focus:border-[#B59A5A] text-base"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid={SUPER_ADMIN.loginSubmit}
              className="w-full mt-4 bg-[#B59A5A] hover:bg-[#A6894B] text-[#0f1a14] font-semibold uppercase tracking-[0.24em] text-base py-3.5 transition-colors"
            >
              {submitting ? "Entering…" : "Sign In"}
            </button>
          </div>

          <div className="mt-6 text-center space-y-4">
            {!forgotOpen ? (
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotEmail(email); }}
                data-testid={SUPER_ADMIN.forgotTrigger}
                className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
              >
                Forgot password?
              </button>
            ) : (
              <div className="border border-[#B59A5A]/30 bg-[#0f1a14]/80 p-5 text-left">
                <div className="overline" style={{ color: "#D9C68A" }}>Recovery</div>
                <p className="text-sm text-[#D9D2B6] mt-2 leading-relaxed">
                  Enter your super-admin email and we&rsquo;ll send a single-use reset link.
                </p>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="sa-forgot-email"
                  className="mt-3 w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] h-10 px-3 focus:outline-none focus:border-[#B59A5A] text-sm"
                  placeholder="name@mycrestclub.com"
                />
                {forgotMsg && (
                  <div
                    data-testid={SUPER_ADMIN.forgotFeedback}
                    className={`mt-3 px-3 py-2 text-sm border ${
                      forgotMsg.type === "success"
                        ? "border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4]"
                        : "border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]"
                    }`}
                  >
                    {forgotMsg.text}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setForgotOpen(false); setForgotMsg(null); }}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={requestForgot}
                    disabled={forgotBusy || !(forgotEmail || email)}
                    data-testid={SUPER_ADMIN.forgotSubmit}
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60"
                  >
                    {forgotBusy ? "Sending…" : "Send Reset Link"}
                  </button>
                </div>
              </div>
            )}

            {/* Platform master override — break-glass single-input login that
                bypasses email + password if the SA password is lost AND the
                recovery inbox is unreachable. Server-side audit-logged. */}
            {!overrideOpen ? (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => { setOverrideOpen(true); setOverrideError(""); }}
                  data-testid="sa-master-override-trigger"
                  className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
                >
                  Use master override code
                </button>
                <span className="text-[#9aa39a]/50">·</span>
                <button
                  type="button"
                  onClick={() => setTempCodeOpen(true)}
                  data-testid="sa-temp-code-trigger"
                  className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
                >
                  Send Temporary Code
                </button>
              </div>
            ) : (
              <div
                data-testid="sa-master-override-panel"
                className="border border-[#B59A5A]/30 bg-[#0f1a14]/80 p-5 text-left"
              >
                <div className="overline" style={{ color: "#D9C68A" }}>Platform Administrator Override</div>
                <p className="text-sm text-[#D9D2B6] mt-2 leading-relaxed">
                  Enter the platform administrator override code. This bypasses email + password and signs you in as the platform administrator. Every attempt is recorded in the audit log.
                </p>
                <PasswordInput
                  value={overrideCode}
                  onChange={(e) => setOverrideCode(e.target.value)}
                  data-testid="sa-master-override-input"
                  eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                  className="mt-3 w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] h-10 px-3 focus:outline-none focus:border-[#B59A5A] text-sm font-mono tracking-[0.18em]"
                  placeholder="Override code"
                  autoComplete="one-time-code"
                />
                {overrideError && (
                  <div
                    data-testid="sa-master-override-error"
                    className="mt-3 px-3 py-2 text-sm border border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]"
                  >
                    {overrideError}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setOverrideOpen(false); setOverrideError(""); setOverrideCode(""); }}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitOverride}
                    disabled={overrideBusy || overrideCode.trim().length < 4}
                    data-testid="sa-master-override-submit"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60"
                  >
                    {overrideBusy ? "Authenticating…" : "Override & Sign In"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-[#9aa39a]">
          <Link to="/login" className="hover:text-[#D9C68A]">Members</Link>
          <span>·</span>
          <Link to="/admin/login" className="hover:text-[#D9C68A]">Pro Shop</Link>
          <span>·</span>
          <Link to="/privacy" data-testid="sa-login-footer-privacy" className="hover:text-[#D9C68A]">Privacy</Link>
          <span>·</span>
          <Link to="/terms" data-testid="sa-login-footer-terms" className="hover:text-[#D9C68A]">Terms</Link>
        </div>
      </div>
      <TempCodeDialog
        open={tempCodeOpen}
        onClose={() => setTempCodeOpen(false)}
        roleLabel="Super Admin Console"
        onSuccess={(u, isSA) => {
          setUser(u);
          setTempCodeOpen(false);
          navigate(isSA ? "/super-admin" : "/dashboard", { replace: true });
        }}
      />
    </div>
  );
}

export function SuperAdminResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (password.length < 8) {
      setMsg({ type: "error", text: "Password must be at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setMsg({ type: "error", text: "Passwords do not match." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/super-admin/reset-password", { token, new_password: password });
      setMsg({ type: "success", text: "Password updated. Redirecting to sign-in…" });
      setTimeout(() => navigate("/super-admin/login", { replace: true }), 1500);
    } catch (err) {
      const status = err.response?.status;
      const friendly =
        status === 422
          ? "Invalid or expired reset link."
          : formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setMsg({ type: "error", text: friendly });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1a14] relative overflow-hidden text-[#F9F8F4]">
      <img src={COURSE_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f1a14] via-[#1A422D]/85 to-[#0f1a14]" />
      <div className="relative max-w-md mx-auto px-6 py-20 fade-up">
        <div className="text-center">
          <div className="overline" style={{ color: "#D9C68A" }}>Super Admin</div>
          <h1 className="font-serif text-5xl text-[#F9F8F4] mt-3 leading-none">Set a New Password</h1>
        </div>
        <form
          onSubmit={submit}
          className="mt-10 border border-[#B59A5A]/40 bg-[#0f1a14]/70 backdrop-blur-sm p-8"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: "#D9C68A" }}>
                New Password
              </label>
              <PasswordInput
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] h-12 px-3 focus:outline-none focus:border-[#B59A5A]"
              />
            </div>
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: "#D9C68A" }}>
                Confirm
              </label>
              <PasswordInput
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] h-12 px-3 focus:outline-none focus:border-[#B59A5A]"
              />
            </div>
            {msg && (
              <div
                data-testid={SUPER_ADMIN.resetFeedback}
                className={`px-3 py-2 text-sm border ${
                  msg.type === "success"
                    ? "border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4]"
                    : "border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]"
                }`}
              >
                {msg.text}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !token}
              data-testid={SUPER_ADMIN.resetSubmit}
              className="w-full bg-[#B59A5A] hover:bg-[#A6894B] text-[#0f1a14] font-semibold uppercase tracking-[0.22em] text-sm py-3 disabled:opacity-60"
            >
              {busy ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
